import useSWR, { mutate } from 'swr';
import { getRequestAsync } from '../helpers/apiHandler';

const createCacheKey = (endpoint, token, extraParams = {}) => {
  if (!token) return null;

  const tokenFingerprint = token.substring(0, 8);
  const queryString = Object.keys(extraParams).length > 0 ? JSON.stringify(extraParams) : '';

  return [endpoint, tokenFingerprint, queryString];
};

/**
 * Force a fresh pull of a WABA's phone numbers, bypassing BOTH the backend
 * in-memory cache (via `&refresh=true`) and the local SWR cache, then write the
 * result back into SWR so every component using this key re-renders.
 *
 * Used to self-heal when a number was deleted directly in Meta's WhatsApp
 * Manager: our caches can't be notified of that, so we re-pull on demand
 * (manual refresh, or after a phone-details fetch fails for a now-missing number).
 */
export const refreshPhoneNumbers = async (waba_id, token) => {
  if (!token || !waba_id) return null;

  const endpoint = `get-phone-numbers?waba_id=${waba_id}`;
  const key = createCacheKey(endpoint, token);

  const response = await getRequestAsync(`/v2/whatsapp/${endpoint}&refresh=true`, {
    headers: { Authorization: token },
  });

  await mutate(key, response, { revalidate: false });
  return response;
};

export const clearWhatsAppCache = () => {
  return mutate(
    (key) => {
      if (!key) return false;
      return Array.isArray(key) && typeof key[0] === 'string' && key[0].startsWith('get-');
    },
    undefined,
    { revalidate: false }
  );
};

export const useWhatsAppData = (endpoint, token, axiosConfig = {}, swrConfig = {}) => {
  const { params = {} } = axiosConfig;

  return useSWR(
    createCacheKey(endpoint, token, params),
    async ([url, _tokenFingerprint, _queryString]) => {
      try {
        const response = await getRequestAsync(`/v2/whatsapp/${url}`, {
          headers: {
            Authorization: token,
            ...(axiosConfig.headers || {}),
          },
          ...axiosConfig,
        });
        return response;
      } catch (error) {
        console.error('Failed to fetch data:', error);
        throw error;
      }
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // Reduced to 1 minute
      revalidateIfStale: true,
      shouldRetryOnError: true,
      errorRetryCount: 3,
      ...swrConfig,
      onError: (error) => {
        if (error.status === 401) {
          console.error('Authentication error fetching data');
        } else if (error.status === 404) {
          console.error('API endpoint not found');
        } else {
          console.error('Error fetching data:', error);
        }
        swrConfig.onError?.(error);
      },
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Don't retry for specific error types
        if (error.status === 404 || error.status === 401) return;
        if (retryCount >= 3) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    }
  );
};
